import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';
import type { Permissions } from '@repo/types/globals';
import type { ReactNode } from 'react';

import { useGetDirectoryPermissions } from '@repo/api/directory/useGetDirectoryPermissions';
import { useGetFilePermissions } from '@repo/api/file/useGetFIlePermissions';
import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { format } from 'date-fns';
import { AlertTriangleIcon, LinkIcon, SquareArrowOutUpRight, User, UserIcon, Users } from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useCopyToClipboard } from '@/hooks/copy-to-clipboard';
import { useFSEntryPermissions } from '@/hooks/fs';
import { useFilesStorage } from '@/store/files-store';
import { getAvatar } from '@/utils/getAvatar';

/**
 * Props for the InfoFSEntry component.
 * @property {boolean} open - Whether the sheet is open.
 * @property {(open: boolean) => void} onOpenChange - Callback for sheet open state.
 * @property {FilePublicWithPermissions | DirectoryPublic} [element] - The file or directory to show info for.
 */
interface InfoFSEntryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  element?: FilePublicWithPermissions | DirectoryPublic;
}

export default function InfoFSEntry(infoFSEntryProps: InfoFSEntryProps) {
  const { open, onOpenChange, element } = infoFSEntryProps;

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent className={'max-w-86'} side={'right'}>
        <SheetBody>
          <SheetHeader className="border-border-primary bg-card border-b">
            <SheetTitle>Szczegóły</SheetTitle>
          </SheetHeader>
          <SheetDescription className={'sr-only'}>Szczegóły o pliku/folderze</SheetDescription>
          <InfoFsEntryContent element={element} />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Props for the InfoFsEntryContent component.
 * @property {FilePublicWithPermissions | DirectoryPublic} [element] - The file or directory to show info for.
 */
interface InfoFsEntryContentProps {
  element?: FilePublicWithPermissions | DirectoryPublic;
}

export function InfoFsEntryContent(infoFsEntryContentProps: InfoFsEntryContentProps) {
  const { element } = infoFsEntryContentProps;
  const { id, permissions, name, createdAt, updatedAt } = element ?? {};
  const isFile = getIsFSEntityFile(element);
  const [, copy] = useCopyToClipboard();
  const { setOpenEditName, setOpenShare } = useFilesStorage();
  const { currentPermission } = useFSEntryPermissions({
    permissions: permissions,
  });
  const filePermissionsQuery = useGetFilePermissions(id, isFile);
  const directoryPermissionsQuery = useGetDirectoryPermissions(id, !isFile);

  const { data, isSuccess, isLoading, isError, errorMessage } = useMemo(() => {
    return isFile ? filePermissionsQuery : directoryPermissionsQuery;
  }, [isFile, filePermissionsQuery, directoryPermissionsQuery]);

  const hasNoSharedEntities: boolean = isSuccess && !data.users.length && !data.groups.length;

  const formattedCreatedAt: string = createdAt ? format(new Date(createdAt), 'd MMMM yyyy') : '';
  const formattedUpdatedAt: string = updatedAt ? format(new Date(updatedAt), 'd MMMM yyyy') : '';

  const rows = useMemo<InfoFsEntryItem[]>(() => {
    return [
      { label: 'Nazwa', value: name },
      { label: 'Typ', value: isFile ? 'Plik' : 'Folder' },
      { label: 'Utworzono', value: formattedCreatedAt },
      { label: 'Edytowano', value: formattedUpdatedAt },
      {
        label: 'Poświadczenia',
        value: permissions ? (
          <Badge size="sm" variant="muted">
            {currentPermission.title}
          </Badge>
        ) : undefined,
      },
      {
        label: 'Link do pliku',
        value:
          isFile && id ? (
            <Badge asChild size="sm" variant="muted" className="cursor-pointer">
              <button
                type="button"
                onClick={() => {
                  void copy(`${window.location.origin}/fs/file/${id}`)
                    .then(() => toast.success('Link skopiowany do schowka'))
                    .catch(() => toast.error('Nie udało się skopiować linku'));
                }}
              >
                <LinkIcon />
                Kopiuj link
              </button>
            </Badge>
          ) : undefined,
      },
    ].filter(Boolean);
  }, [copy, id, name, isFile, formattedCreatedAt, formattedUpdatedAt, permissions, currentPermission]);

  return (
    <div className={'space-y-8 overflow-y-auto p-3'}>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between gap-2">
          <h5 className="text-md font-medium">Szczegóły pliku</h5>
          {!!permissions?.edit && (
            <Button size="sm" variant="linkSecondary" onClick={() => setOpenEditName(true)} aria-label="Edytuj nazwę">
              Edytuj
              <SquareArrowOutUpRight />
            </Button>
          )}
        </div>
        <div className={'space-y-2'}>
          {rows.map((r) => (
            <InfoFsEntryItem key={r.label} label={r.label} value={r.value} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between gap-2">
          <h5 className="text-md font-medium">Uprawnienia</h5>
          {!!permissions?.edit && (
            <Button size="sm" variant="linkSecondary" onClick={() => setOpenShare(true)} aria-label="Edytuj uprawnienia">
              Edytuj
              <SquareArrowOutUpRight />
            </Button>
          )}
        </div>

        <div className="flex w-full flex-col">
          {hasNoSharedEntities && <p className="text-text-tertiary text-sm italic">Brak udostępnionych użytkowników</p>}

          {isSuccess &&
            data.groups.map((group) => (
              <PermissionListElement
                key={group.id}
                permissions={group.permissions}
                data={{
                  title: group.name,
                  avatarURL: group.avatarURL ?? undefined,
                  fallbackIcon: <Users className="size-4" />,
                }}
              />
            ))}

          {isSuccess &&
            data.users.map((user) => (
              <PermissionListElement
                key={user.id}
                permissions={user.permissions}
                data={{
                  title: `${user.name} ${user.surname}`,
                  avatarURL: user.avatarURL ?? undefined,
                  fallbackIcon: <User className="size-4" />,
                }}
              />
            ))}

          {isLoading && (
            <div className="flex items-center gap-2 py-2">
              <Skeleton className="h-10 w-10 rounded-sm" />
              <Skeleton className="h-3 w-14 rounded-sm" />
              <Skeleton className="ml-auto h-3 w-11 rounded-sm" />
            </div>
          )}
          {isError && (
            <Alert
              data-visible={isError}
              variant="destructive"
              className={
                'bg-destructive/5 overflow-hidden transition-all duration-400 data-[visible=false]:max-h-0 data-[visible=false]:opacity-0 data-[visible=true]:max-h-40 data-[visible=true]:opacity-100'
              }
            >
              <AlertTriangleIcon />
              <AlertTitle>{errorMessage.title}</AlertTitle>
              <AlertDescription>{errorMessage.detail}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}

interface InfoFsEntryItem {
  label: string;
  value?: string | ReactNode;
}

function InfoFsEntryItem(infoFsEntryItem: InfoFsEntryItem) {
  const { label, value } = infoFsEntryItem;
  return (
    <div className={'space-y-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <p className={'wrap-break-words w-full text-sm text-wrap'}>{value}</p>
    </div>
  );
}

interface PermissionListElementProps {
  data: { title: string; avatarURL?: string; fallbackIcon?: ReactNode };
  permissions: Permissions;
}

function PermissionListElement(permissionListElementProps: PermissionListElementProps) {
  const { data, permissions } = permissionListElementProps;
  const { title, avatarURL, fallbackIcon } = data;
  const { currentPermission } = useFSEntryPermissions({ permissions });

  return (
    <div className={'flex items-center gap-2 py-1.5'}>
      <Avatar size={'sm'}>
        <AvatarImage src={getAvatar(avatarURL)} />
        <AvatarFallback>{fallbackIcon ? fallbackIcon : <UserIcon className={'size-3.5'} />}</AvatarFallback>
      </Avatar>
      <div className={'overflow-hidden'}>
        <p className={'shrink truncate text-sm'}>{title}</p>
        <p className={'text-muted-foreground text-xs'}>{currentPermission.title}</p>
      </div>
    </div>
  );
}
